import { List, TextField, DateField, NumberField, FunctionField, DateInput } from 'react-admin';
import { useDataProvider } from 'react-admin';
import Datagrid from '../Commons/Datagrid';
import PostPagination from '../Commons/PostPagination';
import { useMediaQuery, createTheme, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import BreadCrumbCustom from '../Commons/BreadCrum';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import historyTracker from '../Commons/historyTracker';
import AuthNotification from '../Commons/AuthNotification';
import { StyleTheme, Style } from '../Commons/Styles.jsx';
import { createStyles, makeStyles } from '@mui/styles';
import FilterSidebar, { ShrinkAwareSelectInput } from '../Commons/FilterSidebar';
import ListActions from '../Commons/ListActions';
import InternalMenu from '../Commons/InternalMenu';
import CircularProgress from '@mui/material/CircularProgress';
// TODO: reemplazar por el ícono definitivo de Bancos cuando esté disponible
import banks from '../Commons/Icons/building.svg';
import requestAuthorization from '../Commons/Icons/colocacion.svg';
// TODO: reemplazar por el ícono definitivo de Imprimir Solicitudes cuando esté disponible
import printRequests from '../Commons/Icons/contabilidad.svg';

import { hasPermissions } from '../Commons/CompanyAuth';

// sp_lista_cbmaesolicitud_PIMP trae codigo_tipo_mov fijo en (1, 3) -- Cheque/Nota de
// Debito, no hace falta el catalogo completo de tipos de movimiento (sp_lista_cbtmovban,
// que ademas incluye Deposito/Nota de Credito, que esta pantalla nunca muestra). Mismo
// mapa (mismos codigos y mismo texto) que ya usa PrintRequestShow.jsx para el campo
// "Tipo" del detalle -- se repite aca en vez de importarlo para no acoplar el listado a
// como esta armado el detalle.
const MovementTypeNames = {
  1: 'Cheque',
  3: 'Nota de Debito',
};

const MovementTypeChoices = Object.entries(MovementTypeNames).map(([id, name]) => ({ id, name }));

// Mismo mecanismo que RequestAuthorizationsList.jsx: reemplazo propio del "empty" del
// Datagrid para conservar el encabezado de columnas cuando el listado devuelve 0 filas.
const PrintRequestsEmpty = () => (
  <Table size="small">
    <TableHead>
      <TableRow>
        <TableCell style={Style.DataGrid}>No. Solicitud</TableCell>
        <TableCell style={Style.DataGrid}>Cuenta</TableCell>
        <TableCell style={Style.DataGrid}>Banco</TableCell>
        <TableCell style={Style.DataGrid}>Tipo</TableCell>
        <TableCell style={Style.DataGrid}>Fecha Documento</TableCell>
        <TableCell style={Style.DataGrid}>Girado</TableCell>
        <TableCell style={Style.DataGrid}>Monto</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      <TableRow>
        <TableCell colSpan={7} style={{ textAlign: 'center' }}>
          No hay solicitudes pendientes de imprimir.
        </TableCell>
      </TableRow>
    </TableBody>
  </Table>
);

// Filtro "Cuenta" dependiente del filtro "Banco": las cuentas bancarias pertenecen cada
// una a un solo banco (cbtaban.banco), asi que en vez de mostrar siempre el catalogo
// completo de cuentas, este combo se acota a las cuentas del banco elegido en el filtro
// "bank" (si hay alguno elegido). useWatch/useFormContext son del mismo react-hook-form
// que ya usa ShrinkAwareSelectInput arriba (ver comentario en FilterSidebar.jsx) para leer
// el valor en vivo del formulario de filtros -- aca se usa ademas para poder LIMPIAR el
// filtro de cuenta cuando el banco cambia y la cuenta elegida ya no pertenece al banco
// nuevo, para no dejar un filtro Banco+Cuenta "imposible" (0 resultados sin que se note
// por que) despues de cambiar el banco.
const AccountFilterInput = ({ accounts, ...rest }) => {
  const bank = useWatch({ name: 'bank' });
  const { setValue, getValues } = useFormContext();

  const filteredAccounts = useMemo(
    () => (bank ? accounts.filter((account) => account.bankName === bank) : accounts),
    [accounts, bank]
  );

  useEffect(() => {
    const currentAccount = getValues('account');
    if (currentAccount && !filteredAccounts.some((account) => account.id === currentAccount)) {
      setValue('account', '', { shouldDirty: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bank]);

  return <ShrinkAwareSelectInput {...rest} choices={filteredAccounts} />;
};

const useStyles = makeStyles(() => createStyles(StyleTheme));

// Listado de solicitudes pendientes de imprimir (sp_lista_cbmaesolicitud_PIMP, via
// Get_Bank_PrintRequests/Banksapi.cs). Se llega aqui desde "Bancos > Imprimir Solicitudes"
// en el menu de Operaciones. La SP ya trae codigo_tipo_mov in (1, 3) (Cheque/Nota de Debito)
// y estatus = 2 (Autorizada) fijos adentro (no son parametros), asi que este listado no
// tiene acciones de autorizar/rechazar -- solo consulta. El "id" de cada fila es simplemente
// el numero de solicitud (ya no un compuesto "codigo_cuenta-numero_solicitud"). rowClick="show"
// abre PrintRequestShow.jsx, que trae su propio detalle (sp_get_cbmaesolicitud_PIMP, via
// Get_Bank_PrintRequest) -- ya no reutiliza sp_devuelve_cbmaesolicitud de Autorizar
// Solicitudes -- y valida el permiso "BankPrintRequest" en vez de "BankRequestAuthorization".
// Esa pantalla de detalle es la que tiene las acciones reales "Imprimir"/"Reimprimir".
const PrintRequestsList = (props) => {
  const classes = useStyles();
  const theme = createTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const [openAuth, setOpenAuth] = useState(false);
  const [auth, setAuth] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [bankChoices, setBankChoices] = useState([]);
  // Mientras esto es true se muestra el spinner de carga inicial (ver el bloque
  // "initialLoading" en el return): esta pantalla necesita traer permisos y el catalogo
  // de cuentas bancarias antes de poder mostrar el listado, y sin este estado la ventana
  // se veia en blanco (solo el breadcrumb) durante esa espera.
  const [initialLoading, setInitialLoading] = useState(true);
  const dataProvider = useDataProvider();

  useEffect(() => {
    (async () => {
      try {
        const currentPath = location.pathname;
        historyTracker.addPath(currentPath, 'Imprimir Solicitudes', 2);

        // Recurso de permisos nuevo ("BankPrintRequest"): requiere que se le den de alta los
        // permisos correspondientes en Auth0 (via Auth/CreatePermissions o el panel de
        // Auth0) antes de que cualquier usuario pueda ver esta pantalla, igual que
        // "BankRequestAuthorization" en RequestAuthorizationsList.jsx.
        const authdata = { data: hasPermissions('BankPrintRequest', 'list') };
        if (authdata.data.length > 0) {
          setAuth(authdata.data[0]);
          setOpenAuth(!authdata.data[0]);

          if (authdata.data[0]) {
            // provider/bankaccounts (sp_lista_cbtaban_simple) es el mismo catalogo de cuentas
            // bancarias de la empresa que ya usa RequestAuthorizationShow.jsx para "Cuenta
            // Bancaria". sp_lista_cbmaesolicitud_PIMP no devuelve codigo_cuenta (ver
            // SP_SolicitudImpresion en Models.cs), asi que los choices de "Cuenta"/"Banco" se
            // arman con accountNumber/bankName (los mismos campos de texto que ya trae cada
            // fila del listado) en vez del codigo interno, y el filtro se resuelve en memoria
            // en Get_Bank_PrintRequests comparando esos mismos textos.
            const bankAccountsData = await dataProvider.getList('provider/bankaccounts', { filter: {}, pagination: {}, sort: {} }).catch(() => ({ data: [] }));

            const seenAccounts = new Set();
            const accountsList = [];
            const seenBanks = new Set();
            const banksList = [];
            (bankAccountsData.data || []).forEach((item) => {
              if (item.accountNumber && !seenAccounts.has(item.accountNumber)) {
                seenAccounts.add(item.accountNumber);
                // bankName se conserva en cada cuenta (ademas de id/name) para poder
                // filtrarlas por el banco elegido en AccountFilterInput.
                accountsList.push({ id: item.accountNumber, name: [item.accountNumber, item.accountName].filter(Boolean).join(' - '), bankName: item.bankName });
              }
              if (item.bankName && !seenBanks.has(item.bankName)) {
                seenBanks.add(item.bankName);
                banksList.push({ id: item.bankName, name: item.bankName });
              }
            });
            setAccounts(accountsList);
            setBankChoices(banksList);
          }
        } else {
          setAuth(false);
          setOpenAuth(true)
        }
      }
      catch {
      }
      finally {
        setInitialLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataProvider, location.pathname]);

  // sp_lista_cbmaesolicitud_PIMP no admite ningun filtro adicional (ver comentario de la
  // funcion mas abajo), asi que Banco/Cuenta/Tipo/Fecha se resuelven en memoria en
  // Get_Bank_PrintRequests/Banksapi.cs, igual que Tipo/Moneda en RequestAuthorizationsList.jsx.
  // Banco va primero porque Cuenta depende de el (ver AccountFilterInput): cada cuenta
  // pertenece a un solo banco, asi que elegir un banco acota el combo de cuentas a las
  // que son de ese banco. "Tipo" usa el mismo par de choices fijo (Cheque/Nota de Debito,
  // MovementTypeChoices arriba) que la columna, no el catalogo completo de
  // bank/movementtype: esta SP nunca trae otro tipo.
  const PostFilter = [
    <ShrinkAwareSelectInput InputProps={{ disableUnderline: true }} label={<span>{"Banco"}</span>} source="bank" choices={bankChoices} alwaysOn />,
    <AccountFilterInput InputProps={{ disableUnderline: true }} label={<span>{"Cuenta"}</span>} source="account" accounts={accounts} alwaysOn />,
    <ShrinkAwareSelectInput InputProps={{ disableUnderline: true }} label={<span>{"Tipo"}</span>} source="movementType" choices={MovementTypeChoices} alwaysOn />,
    // variant="filled"/fullWidth explicitos en los dos DateInput de abajo: por las dudas,
    // ademas del ajuste de tema (ver comentario de RaDateInput/MuiFilledInput.sizeSmall en
    // FilterSidebar.jsx -- ese es el que de verdad soluciona el alto y el ancho, esto es
    // solo un respaldo).
    <DateInput variant="filled" fullWidth InputProps={{ disableUnderline: true }} label={<span>{"Fecha Desde"}</span>} source="dateStart" alwaysOn />,
    <DateInput variant="filled" fullWidth InputProps={{ disableUnderline: true }} label={<span>{"Fecha Hasta"}</span>} source="dateEnd" alwaysOn />,
  ];

  const handleRedirectRequestAuthorizations = () => {
    navigate(`/bank/requestauthorization`);
  };

  const handleRedirectPrintRequests = () => {
    navigate(`/bank/printrequests`);
  };

  const [openBanks, setOpenBanks] = useState(true);

  const handleClickOpenBanks = () => {
    setOpenBanks(!openBanks);
  };

  const menus = [{
    icon: <img src={banks} alt="logo" style={Style.MenuLogo} />, name: 'Bancos',
    open: openBanks,
    openAction: handleClickOpenBanks,
    actions: [
      { icon: <img src={requestAuthorization} alt="logo" style={Style.MenuLogo} />, name: 'Autorizar Solicitudes', onClick: handleRedirectRequestAuthorizations },
      { icon: <img src={printRequests} alt="logo" style={Style.MenuLogo} />, name: 'Imprimir Solicitudes', onClick: handleRedirectPrintRequests },
    ]
  }];

  let pagination = <PostPagination menu={<InternalMenu menus={menus} />} />;

  return (
    <div class="container">
      <BreadCrumbCustom history={historyTracker.getHistory()} />
      {initialLoading ? (
        // Spinner de carga inicial: reutiliza la misma clase "Loading" (index.css) y el
        // mismo CircularProgress que se usa en otras pantallas para la espera de guardado,
        // pero aqui cubre la espera de auth + catalogo de cuentas bancarias, que antes
        // dejaba la pantalla en blanco.
        <div class="Loading" style={{ display: 'block' }}>
          <CircularProgress size="15rem" />
        </div>
      ) : auth ? (
        <div class="row">
          <div class="col-12">
            {isSmall ? (
              <List empty={false} {...props} actions={<ListActions download />} bulkActionButtons={false} pagination={pagination}>
                <Datagrid {...props} empty={false}>
                </Datagrid>
              </List>
            ) : (
              <List empty={false} perPage={10} {...props} aside={<FilterSidebar {...props} filters={PostFilter} />} actions={<ListActions download />} bulkActionButtons={false} pagination={pagination}>
                <Datagrid {...props} rowClick="show" style={Style.DataGrid} empty={<PrintRequestsEmpty />}>
                  <TextField source="requestNumber" style={Style.fieldList} label="No. Solicitud" cellClassName={classes.fields} />
                  <FunctionField source="accountNumber" style={Style.fieldList} label="Cuenta" cellClassName={classes.fields} render={record => [record.accountNumber, record.accountName].filter(Boolean).join(' - ')} />
                  <TextField source="bankName" style={Style.fieldList} label="Banco" cellClassName={classes.fields} />
                  <FunctionField source="movementTypeCode" style={Style.fieldList} label="Tipo" cellClassName={classes.fields} render={record => MovementTypeNames[record.movementTypeCode] || ''} />
                  <DateField source="documentDate" style={Style.fieldList} label="Fecha Documento" cellClassName={classes.fields} />
                  <TextField source="payableTo" style={Style.fieldList} label="Girado" cellClassName={classes.fields} />
                  <NumberField source="amount" style={Style.fieldList} label="Monto" cellClassName={classes.fields} options={{ style: 'decimal', minimumFractionDigits: 2 }} />
                </Datagrid>
              </List>
            )}
          </div>
        </div>) : (<AuthNotification openerror={openAuth} severity={'error'} message={'No posee los permisos para poder interactuar con esta vista'} />)}
    </div>
  )
};

export default PrintRequestsList;
